import { INamedFrameModel, IScheduleModel, ISelectList, IShowable, ISubRoute, IValueElement, IValueFrameModel } from '@caslink/common-client';
import { CardStatus } from '@caslink/common-client/ng-app/alerts/typings/CardStatus';
import { DeviceModelInitializationOptions } from '../../device-model/device-model-initialization-options.interface';
import { IControlInput, IDeviceInformation, IDeviceNode, ISelectListControlInput, NumberSensorInputMap } from './device';
import FirmwareImageStatus = CASLink.Device.Ui.ViewModel.Device.FirmwareImageStatus;
import { GraphicBehaviorType } from './graphic-behavior-type.enum';

/* Setpoint stuff */

export const enum ControlMode {
    Off = 0,
    // <summary>
    // An enum constant representing the external option.
    // </summary>
    External = 1,
    // <summary>
    // An enum constant representing the heating cooling option.
    // </summary>
    FullControl = 2,
    // <summary>
    // An enum constant representing the heating option.
    // </summary>
    FullControlHeatOnly = 3,
    // <summary>
    // An enum constant representing the cooling option.
    // </summary>
    FullControlCoolOnly = 4,
    // <summary>
    // An enum constant representing the emergency heat option.
    // </summary>
    EmergencyHeat = 7
}

export const enum ElementTypes {
    DateTimeSensor = 'DateTimeSensor',
    DeviceCategory = 'DeviceCategory',
    DeviceSection = 'DeviceSection',
    DynamicCalendar = 'DynamicCalendar',
    ElementList = 'ElementList',
    EnumerationSensor = 'EnumerationSensor',
    Root = 'Root'
}

export const enum RelationshipTypes {
    DynamicElementList = 'DynamicElementList',
    SelectList = 'SelectList'
}

export type ControlModeString = 'Off' | 'External' | 'FullControl' | 'FullControlHeatOnly' | 'FullControlCoolOnly' | 'EmergencyHeat';

export const enum BlowerMode {
    Auto = 0,
    On = 1,
}

export const enum StagingMode {
    Off = 0,
    Heating = 1,
    Cooling = 2
}

/* End Setpoint stuff */

export interface IViewModelConfirmationSubscription {
    subscriptionState: IViewModelConfirmationSubscriptionState;
}

export interface ICardDataViewModel extends IShowable {
    data?: IDeviceNode;
    id?: number;
    isCategory?: boolean;
    isSection?: boolean;
    isEnabled?: boolean;
    name: string;
    value: string;
    isGraphicBehavior?: boolean;
    sensorId?: number;
    numberSensorInputMap?: NumberSensorInputMap;
    indent?: number;
    order?: number;
    hidableKeys?: string[];
    $controlInput?: IControlInput;
    graphicBehavior?: GraphicBehaviorType | string;
    displayValue?: string;
    actualValue?: string;
    categories?: string[];
}

export interface OccupancyInterface {
    occupiedOn?: boolean,
    occupiedVisible?: boolean,
    occupiedEnabled?: boolean,
    occupiedSensorId?: number,
    occupiedName?: string;
}


export interface DisplayInfoEditableProperties {
    occupancy?: OccupancyInterface;
    occupiedCoolingSetpoint?: number;
    occupiedHeatingSetpoint?: number;
    unoccupiedCoolingSetpoint?: number;
    unoccupiedHeatingSetpoint?: number;
}

export interface TextWithUnitsInterface {
    text: string;
    units: string;
}

export interface IDisplayInfo {
    relativeHumidity: TextWithUnitsInterface;
    temperature: TextWithUnitsInterface;
    dataViewModel: ICardDataViewModel[];
    isCardEditable: boolean;
    occupiedSetpointMinimum?: number;
    occupiedSetpointMaximum?: number;
    unoccupiedSetpointMinimum?: number;
    unoccupiedSetpointMaximum?: number;
    occupiedCoolingSetpoint?: number;
    occupiedHeatingSetpoint?: number;
    unoccupiedCoolingSetpoint?: number;
    unoccupiedHeatingSetpoint?: number;
    occupiedCoolingSetpointSensorId?: number;
    occupiedHeatingSetpointSensorId?: number;
    unoccupiedCoolingSetpointSensorId?: number;
    unoccupiedHeatingSetpointSensorId?: number;
    occupancy?: OccupancyInterface;
    editableProperties: DisplayInfoEditableProperties;
    dirty?: boolean;
    editProperties: any;
}

export interface ICardStatus {
    icon?: string;
    materialIcon?: string;
    klass: string; // because class is a protected keyword
    statusText: CardStatus;
}

export interface IControlDeviceModel extends CASLink.Device.Ui.ViewModel.DynamicControls.ControlDeviceViewModel {
    controls: IControlModel[];
}

export interface IControlModel extends CASLink.Device.Ui.ViewModel.DynamicControls.ControlViewModel {
    controlSummaryViewModel: CASLink.Device.Ui.ViewModel.DynamicControls.ControlSummaryViewModel;

    confirmationSubscription: IViewModelConfirmationSubscription;
    displayInfo: IDisplayInfo;
    setpointModel: ISetpointModel;
    status: ICardStatus;
    stopLiveModelUpdates: () => void;
    startLiveModelUpdates: () => void;
    id: number;
    text: string;
}

export interface IControlSubRoute extends ISubRoute {
    isEnabled: (control: IControlModel) => boolean;
    isVisible: (control: IControlModel) => boolean;
}

export interface IControlSubRoutes {
    auditingState: IControlSubRoute;
    currentInfoState: IControlSubRoute;
    scheduleState: IControlSubRoute;
    systemSettingsState: IControlSubRoute;
}

export interface IControlZoneModel extends CASLink.Device.Ui.ViewModel.DynamicControls.ControlZoneViewModel {
    icon?: string;
}

export interface ICurrentInfoChangeConfirmationData {
    hardwareItemId: number;
    locationId: number;
    deviceInfo: IDeviceInformation;
    note: string;
}

export interface IDeviceChangeConfirmationData {
    hardwareItemId: number;
    locationId: number;
    deviceInfo: IDeviceInformation;
    note: string;
}

export interface IScheduleChangeConfirmationData {
    hardwareItemId: number;
    locationId: number;
    deviceInfo: IDeviceInformation;
    note: string;
}

export interface IDashboardChangeConfirmationData {
    hardwareItemId: number;
    locationId: number;
    deviceInfo: IDeviceInformation;
    note: string;
    name: string;
    action: DeviceUpdateAction;
}

export enum DeviceUpdateAction {
    CONTROL_SUMMARY,
    CURRENT_INFORMATION,
    DEVICE_INFORMATION,
    SCHEDULE,
}

export interface IDeviceModel {
    controls: IControlModel[];
    descriptor: string;
    modelName: string;
    initialize: (options: DeviceModelInitializationOptions) => void;
    setup: (control: IControlModel) => any;
    setupCards: (data: IControlModel[]) => void;
    subroutes: IControlSubRoutes;
    updateCard: (control: IControlModel, viewModel: CASLink.Device.Ui.ViewModel.DynamicControls.ControlSummaryViewModel) => any;
    updateStatus: (control: IControlModel, status: string) => any;
    updateWarning?: string;
}

export interface IOverrideZoneInputs {
    fansOn?: ISelectListControlInput;
    fansOnByBmsMaxAir?: ISelectListControlInput;
    lightsOn?: ISelectListControlInput;
    prepModeOn?: ISelectListControlInput;
    washOn?: ISelectListControlInput;
}

export interface IEcpmScheduleModel extends IScheduleModel {
    frames: IValueFrameModel[];
}

export interface IRtuControlModel extends IControlModel {
    currentStagingMode?: number;
    setToReadyTimer?: number;
}

export interface IRtuCurrentInfoViewModel {
    applyToArea: IValueElement<boolean>;
    applyToSite: IValueElement<boolean>;
    controlModeSelections: ISelectList<string>;
}

export interface IRtuScheduleModel extends IScheduleModel {
    frames: INamedFrameModel[];
    setpoints?: IScheduleSetpointModel[];
}

/**
 * Data contract for CASLink.Web.Core.Models.Controls.SetpointModel
 */
export interface IScheduleSetpointModel extends ISetpointModel {
    color?: string;
}

export interface ISensorDetailModel {
    sensorId?: number;
    sensorName?: string;
    sensorCategory?: string;
    sensorType?: string;
    sensorValue?: number;

    /* Extra properties that were used in ControlRTUModel -- looks like a double export interface declaration */
    sensorDisplayValue?: string;
    isVisible?: boolean;

    /* Extra properties that were hardcoded in ControlRTUModel -- better for them to be here */
    postfix?: string;
    isOnOff?: boolean;
}

export interface ISetpointModel {
    controlMode?: ControlMode;
    blowerMode?: BlowerMode;
    occupied?: number;
    heatingTemp?: string;
    coolingTemp?: string;
    stagingMode?: StagingMode;
    useExternalEfficiencySetpoints?: number;
    name?: string;
}

export interface IViewModelConfirmationSubscriptionState {
    originalViewModel: any;
    changedViewModel: any;
}

/** ECPM specfiic */
export interface IZoneCurrentInfoViewModel {
    fansOn: IValueElement<boolean>;
    fansOnByBmsMaxAir: IValueElement<boolean>;
    lightsOn: IValueElement<boolean>;
    prepModeOn: IValueElement<boolean>;
    washOn: IValueElement<boolean>;
}

export interface IDeviceStatusBulkTemplateUpdateResult {
    hardwareVersion?: number;
    modbusId?: number;
    configurationId?: string;
}


export interface IBusyFirmwareStatusViewInfo {
    status: FirmwareImageStatus.Busy;
    currentPage?: number;
    totalPages?: number;
}

export interface IFirmwareStatusViewInfo extends IStatusViewInfo {
    status: FirmwareImageStatus.BadImage |
    FirmwareImageStatus.Current |
    FirmwareImageStatus.NoImage |
    FirmwareImageStatus.OnDeck |
    FirmwareImageStatus.Previous |
    FirmwareImageStatus.Ready;
}

export interface IStatusViewInfo {
    cls: string;
    icon: string;
    size: number;
    statusDescription: string;
}

export interface IModbusDeviceFirmwareVersion {
    version: number;
}

